import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Tooltip,
  Paper,
  Box
} from '@mui/material';
import { Edit, Delete, Send, Close } from '@mui/icons-material';

const UsersTable = ({
  filteredUsers,
  page,
  rowsPerPage,
  currentUser,
  onEditRoles,
  onDeleteUser,
  onResendInvitation,
  onCancelInvitation,
  getRoleDisplayName,
  colors,
  onChangePage,
  onChangeRowsPerPage,
  t
}) => {
  // Gets the role color based on role name
  const getUserManagementRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return colors.secondary;
      case 'billing_admin':
        return colors.info;
      default:
        return colors.backgroundAlt;
    }
  };

  const getStatusChip = (user) => {
    if (user.invitation_status === 'pending') {
      return (
        <Chip
          label={t('pending', 'Pending')}
          size="small"
          sx={{
            fontWeight: 500,
            fontSize: '0.7rem',
            backgroundColor: '#fef3c7',
            color: '#92400e',
          }}
        />
      );
    }
    return (
      <Chip
        label={user.is_allowed ? t('active', 'Active') : t('inactive', 'Inactive')}
        color={user.is_allowed ? 'success' : 'default'}
        size="small"
        sx={{
          fontWeight: 500,
          fontSize: '0.7rem',
          backgroundColor: user.is_allowed ? colors.secondaryLight : colors.backgroundAlt,
          color: user.is_allowed ? colors.secondary : colors.textSecondary
        }}
      />
    );
  };

  const isPending = (user) => user.invitation_status === 'pending';

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '16px',
        overflow: 'hidden',
        border: `1px solid ${colors.border}`,
        mb: 3
      }}
    >
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: colors.backgroundAlt }}>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: colors.text,
                  py: 2.5
                }}
              >
                {t('name', 'Name')}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: colors.text,
                  py: 2.5
                }}
              >
                {t('email', 'Email')}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: colors.text,
                  py: 2.5
                }}
              >
                {t('phone', 'Phone')}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: colors.text,
                  py: 2.5
                }}
              >
                {t('roles', 'Roles')}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: colors.text,
                  py: 2.5
                }}
              >
                {t('user_status', 'Status')}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: colors.text,
                  py: 2.5
                }}
              >
                {t('last_activity', 'Last Activity')}
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 600,
                  color: colors.text,
                  py: 2.5
                }}
              >
                {t('actions', 'Actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((user) => (
                <TableRow key={user.id} sx={{
                  '&:hover': { backgroundColor: colors.backgroundAlt },
                  transition: 'background-color 0.2s',
                  opacity: isPending(user) ? 0.75 : 1,
                }}>
                  <TableCell sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {isPending(user)
                        ? <span style={{ color: colors.textSecondary, fontStyle: 'italic' }}>{t('pending_invitation', 'Pending invitation')}</span>
                        : <>{user.first_name} {user.last_name}</>
                      }
                      {!isPending(user) && user.id === currentUser.id && (
                        <Chip
                          label={t('you', 'You')}
                          size="small"
                          sx={{
                            ml: 1,
                            backgroundColor: colors.primary,
                            color: 'white',
                            fontWeight: 500,
                            fontSize: '0.7rem'
                          }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 2, color: colors.textSecondary }}>
                    {user.email}
                  </TableCell>
                  <TableCell sx={{ py: 2, color: colors.textSecondary }}>
                    {user.phone_number || '-'}
                  </TableCell>
                  <TableCell sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {user.roles.map((role) => (
                        <Chip
                          key={role}
                          label={getRoleDisplayName(role)}
                          size="small"
                          sx={{
                            backgroundColor: getUserManagementRoleColor(role),
                            color: role === 'user' ? colors.text : 'white',
                            fontWeight: 500,
                            fontSize: '0.7rem'
                          }}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 2 }}>
                    {getStatusChip(user)}
                  </TableCell>
                  <TableCell sx={{ py: 2, color: colors.textSecondary }}>
                    {isPending(user)
                      ? (user.invited_at
                        ? t('invited_on', 'Invited') + ' ' + new Date(user.invited_at).toLocaleDateString()
                        : '-')
                      : (user.last_activity
                        ? new Date(user.last_activity).toLocaleString()
                        : t('never', 'Never'))
                    }
                  </TableCell>
                  <TableCell sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex' }}>
                      {isPending(user) ? (
                        <>
                          <Tooltip title={t('resend_invitation', 'Resend Invitation')}>
                            <span>
                              <IconButton
                                onClick={() => onResendInvitation(user)}
                                size="small"
                                sx={{
                                  mr: 1,
                                  color: colors.primary,
                                  backgroundColor: colors.primaryLight,
                                  '&:hover': {
                                    backgroundColor: colors.primary,
                                    color: 'white'
                                  },
                                }}
                              >
                                <Send fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={t('cancel_invitation', 'Cancel Invitation')}>
                            <span>
                              <IconButton
                                onClick={() => onCancelInvitation(user)}
                                size="small"
                                sx={{
                                  color: colors.error,
                                  backgroundColor: colors.errorLight,
                                  '&:hover': {
                                    backgroundColor: colors.error,
                                    color: 'white'
                                  },
                                }}
                              >
                                <Close fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <Tooltip title={t('edit_roles', 'Edit Roles')}>
                            <span>
                              <IconButton
                                onClick={() => onEditRoles(user)}
                                disabled={user.id === currentUser.id}
                                size="small"
                                sx={{
                                  mr: 1,
                                  color: colors.primary,
                                  backgroundColor: colors.primaryLight,
                                  '&:hover': {
                                    backgroundColor: colors.primary,
                                    color: 'white'
                                  },
                                  '&.Mui-disabled': {
                                    opacity: 0.4,
                                    backgroundColor: colors.backgroundAlt
                                  }
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={t('delete_user', 'Delete User')}>
                            <span>
                              <IconButton
                                onClick={() => onDeleteUser(user)}
                                disabled={user.id === currentUser.id}
                                size="small"
                                sx={{
                                  color: colors.error,
                                  backgroundColor: colors.errorLight,
                                  '&:hover': {
                                    backgroundColor: colors.error,
                                    color: 'white'
                                  },
                                  '&.Mui-disabled': {
                                    opacity: 0.4,
                                    backgroundColor: colors.backgroundAlt
                                  }
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredUsers.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
        labelRowsPerPage={t('rows_per_page', 'Rows per page:')}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} ${t('of', 'of')} ${count}`
        }
        sx={{
          borderTop: `1px solid ${colors.border}`,
          color: colors.textSecondary,
          '& .MuiTablePagination-select': {
            borderRadius: '8px',
          },
          '& .MuiTablePagination-selectIcon': {
            color: colors.textSecondary
          }
        }}
      />
    </Paper>
  );
};

export default UsersTable;
